import { defaultThemeV2 } from "../defaultThemeV2";
import { deepMerge } from "./deepMerge";
import { mapV1ToV2 } from "./mapV1ToV2";
import { BuildSurveyThemeResult, DeepPartial } from "../types";
import { ThemeObjectInterFace, ThemeV2 } from "../../util/types";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isV2Payload = (payload: unknown): payload is DeepPartial<ThemeV2> =>
  isObject(payload) && payload.theme_version === "v2";

const hasV1Signals = (
  payload: unknown,
): payload is Partial<ThemeObjectInterFace> =>
  isObject(payload) &&
  [
    "main_color",
    "secondary_color",
    "text_color",
    "error_color",
    "accent_color",
    "icon_image",
    "background_image",
    "line_1",
    "line_2",
    "redirect_message",
    "close_message",
    "disqualification_message",
    "hints",
    "send_button",
    "orientation",
    ...(isV2Payload(payload)
      ? []
      : ["show_progress", "show_grid_lines", "transition_messages"]),
  ].some((k) => k in payload);

const toLegacyTheme = (theme: ThemeV2): ThemeObjectInterFace => ({
  main_color: theme.tokens.color.action.primary,
  secondary_color: theme.tokens.color.surface.card,
  text_color: theme.tokens.color.text.primary,
  error_color: theme.tokens.color.feedback.error,
  accent_color: theme.tokens.color.text.accent,
  icon_image: theme.assets.header.icon,
  line_1: theme.copy.header.line1,
  line_2: theme.copy.header.line2,
  background_image: theme.assets.chat.background,
  redirect_message: theme.copy.redirect,
  close_message: theme.copy.close,
  disqualification_message: theme.copy.disqualification,
  orientation: theme.layout.orientation,
  hints: {
    waiting: theme.copy.hints.waiting,
    multichoice: theme.copy.hints.multichoice,
    multiselect: theme.copy.hints.multiselect,
    others: theme.copy.hints.others,
    multitext: theme.copy.hints.multitext,
  },
  send_button: theme.copy.actions.send,
  show_progress: theme.show_progress,
  show_grid_lines: theme.show_grid_lines,
  transition_messages: theme.transition_messages,
});

export const buildSurveyTheme = (payload: unknown): BuildSurveyThemeResult => {
  const themePayload =
    isObject(payload) && isObject(payload.theme) ? payload.theme : payload;

  const v2Input: DeepPartial<ThemeV2> = isV2Payload(themePayload)
    ? themePayload
    : {};
  const v1Mapped = hasV1Signals(themePayload)
    ? mapV1ToV2(themePayload)
    : ({} as DeepPartial<ThemeV2>);

  let source: BuildSurveyThemeResult["source"] = "v1-mapped";
  if (isV2Payload(themePayload) && hasV1Signals(themePayload)) source = "mixed";
  else if (isV2Payload(themePayload)) source = "v2";

  // precedence: explicit v2 > v1 mapped > defaults
  const withV1 = deepMerge(defaultThemeV2, v1Mapped);
  // Older v2 themes also kept loading messages at the root.
  const withLegacyTransitions = deepMerge(withV1, {
    copy: { status: { transitions: v2Input.transition_messages } },
  });
  const normalizedTheme = deepMerge(withLegacyTransitions, v2Input);
  normalizedTheme.transition_messages = normalizedTheme.copy.status.transitions;

  return {
    normalizedTheme,
    legacyTheme: toLegacyTheme(normalizedTheme),
    source,
  };
};
